import planLimitsData from '../data/planLimits.json';
import { UserPreferences } from './supabase';
import { Car } from './supabase';

type PlanKey = 'free' | 'pro';

// Cast to include car_creation_limit
const planLimits = planLimitsData as Record<PlanKey, {
    storage_size_limit: number;
    file_size_limit: number;
    daily_upload_limit: number;
    hourly_upload_limit: number;
    file_count_limit: number;
    email_parsing_limit: number;
    file_parsing_limit: number;
    car_creation_limit: number;
    ai_daily_limit: number;
}>;

/**
 * Returns the effective car creation limit for a user.
 * - If user_preferences.car_creation_limit > 0 → use that (individual override)
 * - Otherwise → use plan default from planLimits.json
 */
export function getCarCreationLimit(preferences: UserPreferences | null): number {
    // Individual override takes priority
    if (preferences?.car_creation_limit && preferences.car_creation_limit > 0) {
        return preferences.car_creation_limit;
    }

    // Fall back to plan default
    const planType = (preferences?.plan_type || 'free') as PlanKey;
    const plan = planLimits[planType] || planLimits['free'];
    return plan.car_creation_limit;
}

/**
 * Returns the current car limit status for a user.
 */
export function getCarLimitStatus(
    cars: Car[],
    preferences: UserPreferences | null
): {
    current: number;
    limit: number;
    isReached: boolean;
    planType: string;
} {
    // Only count cars OWNED by the user (not shared/car_admin cars) and NOT archived
    const ownedCars = cars.filter(car => car.is_owner !== false && car.is_archived !== true);
    const current = ownedCars.length;
    const limit = getCarCreationLimit(preferences);
    const isReached = current >= limit;
    const planType = preferences?.plan_type || 'free';

    return { current, limit, isReached, planType };
}

/**
 * Returns the effective AI daily limit for a user.
 * - If user_preferences.ai_daily_limit > 0 → use that (individual override)
 * - Otherwise → use plan default from planLimits.json
 */
export function getAIDailyLimit(preferences: UserPreferences | null): number {
    // Individual override takes priority
    if (preferences?.ai_daily_limit && preferences.ai_daily_limit > 0) {
        return preferences.ai_daily_limit;
    }

    // Fall back to plan default
    const planType = (preferences?.plan_type || 'free') as PlanKey;
    const plan = planLimits[planType] || planLimits['free'];
    return plan.ai_daily_limit || 20;
}
